import React, { useMemo } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ErrorDisplay } from './ErrorDisplay';
import { ErrorSeverity } from '@services/errorHandling';

interface NetworkErrorProps {
    error: FetchBaseQueryError | SerializedError | undefined;
    onRetry?: () => void;
    variant?: 'inline' | 'page';
}

/**
 * Specialized error display for RTK Query errors
 *
 * Usage:
 * ```tsx
 * const { data, error, isLoading } = useGetPurchasesQuery({ year, concert });
 *
 * if (error) return <NetworkError error={error} onRetry={refetch} />;
 * ```
 */
export const NetworkError: React.FC<NetworkErrorProps> = ({
    error,
    onRetry,
    variant = 'inline',
}) => {
    if (!error) return null;

    // Extract useful information from RTK Query error
    const errorDetails = useMemo(() => {
        if ('status' in error) {
            // FetchBaseQueryError
            const status = error.status;

            if (status === 401) {
                return {
                    title: 'Authentication Required',
                    message: 'Your session has expired. Please log in again.',
                    severity: ErrorSeverity.WARNING,
                };
            }

            if (status === 403) {
                return {
                    title: 'Access Denied',
                    message: 'You do not have permission to access this resource.',
                    severity: ErrorSeverity.ERROR,
                };
            }

            if (status === 404) {
                return {
                    title: 'Not Found',
                    message: 'The requested data could not be found.',
                    severity: ErrorSeverity.WARNING,
                };
            }

            if (status === 500 || status === 502 || status === 503) {
                return {
                    title: 'Server Error',
                    message: 'The server encountered an error. Please try again later.',
                    severity: ErrorSeverity.ERROR,
                };
            }

            if (status === 'FETCH_ERROR') {
                return {
                    title: 'Connection Error',
                    message: 'Unable to connect to the server. Please check your internet connection.',
                    severity: ErrorSeverity.ERROR,
                };
            }

            if (status === 'PARSING_ERROR') {
                return {
                    title: 'Data Error',
                    message: 'The server returned invalid data. Please try again.',
                    severity: ErrorSeverity.ERROR,
                };
            }

            if (status === 'TIMEOUT_ERROR') {
                return {
                    title: 'Request Timeout',
                    message: 'The request took too long. Please try again.',
                    severity: ErrorSeverity.WARNING,
                };
            }

            return {
                title: 'Network Error',
                message: `Request failed with status ${status}`,
                severity: ErrorSeverity.ERROR,
            };
        }

        // SerializedError
        return {
            title: 'Request Failed',
            message: error.message || 'An unexpected error occurred',
            severity: ErrorSeverity.ERROR,
        };
    }, [error]);

    const { title, message, severity } = errorDetails;

    return (
        <ErrorDisplay
            error={error}
            title={title}
            message={message}
            severity={severity}
            onRetry={onRetry}
            variant={variant}
        />
    );
};
